// src/pages/Vouchers/VoucherPrint.jsx
// نسخة الطباعة لكل أنواع السندات — A4، مع التفقيط وتفصيل الضريبة
import React, { forwardRef } from 'react';
import dayjs from 'dayjs';
import ReportPrintHeader from '../../components/ReportPrintHeader';
import { tafqeet } from '../../utils/tafqeet';

const TYPES = {
  101: { name: 'سند قبض من عميل', receipt: true },
  102: { name: 'سند صرف لمورد', receipt: false },
  103: { name: 'سند قبض عام', receipt: true },
  104: { name: 'سند صرف عام', receipt: false },
  105: { name: 'سند صرف ضريبة', receipt: false },
};

const money = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// الـ API: 1 نقدي — 2 بنكي (القيمة 0 من نسخ قديمة = نقدي)
const paymentName = (t) => (Number(t) === 2 ? 'بنكي' : 'نقدي');

const C = { ink: '#111827', muted: '#6b7280', line: '#d1d5db', soft: '#f3f4f6' };
const cell = { border: `1px solid ${C.line}`, padding: '7px 10px', fontSize: 13.5, verticalAlign: 'middle' };
const th = { ...cell, background: C.soft, fontWeight: 700, width: '22%', whiteSpace: 'nowrap' };

const VoucherPrint = forwardRef(({ voucher, companyInfo }, ref) => {
  if (!voucher) return null;

  const t = TYPES[Number(voucher.voucherType)] || { name: 'سند', receipt: false };
  const net = Number(voucher.amount) || 0;
  const tax = Number(voucher.taxAmount) || 0;
  const total = voucher.totalAmount != null ? Number(voucher.totalAmount) : net + tax;
  const hasTax = Number(voucher.voucherType) === 105 || tax > 0;
  const party = voucher.customerName || voucher.supplierName || voucher.accountName || '';
  const date = voucher.voucherDate ? dayjs(voucher.voucherDate).format('DD/MM/YYYY') : '—';
  const signatures = t.receipt ? ['المحاسب', 'أمين الصندوق', 'الدافع'] : ['المحاسب', 'المدير المالي', 'المستلم'];

  return (
    <div ref={ref} dir="rtl" className="voucher-print" style={{
      direction: 'rtl', background: '#fff', color: C.ink, padding: '8mm 10mm',
      fontFamily: 'Tajawal, Cairo, "Segoe UI", Tahoma, sans-serif', boxSizing: 'border-box',
      minHeight: '277mm', display: 'flex', flexDirection: 'column',
    }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .voucher-print { min-height: auto !important; }
        }
        .voucher-print table { border-collapse: collapse; width: 100%; }
        .voucher-print * { box-sizing: border-box; }
      `}</style>

      <ReportPrintHeader title={t.name} subTitle={`رقم السند: ${voucher.voucherNumber || '—'}`} company={companyInfo} />

      {/* شريط العنوان */}
      <div style={{
        display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 12,
        margin: '10px 0 14px', border: `2px solid ${C.ink}`, borderRadius: 8, overflow: 'hidden',
      }}>
        <div style={{ padding: '10px 14px', fontSize: 20, fontWeight: 800, display: 'flex', alignItems: 'center' }}>
          {t.name}
        </div>
        <div style={{ display: 'flex' }}>
          {[['رقم السند', voucher.voucherNumber || '—'], ['التاريخ', date]].map(([k, v]) => (
            <div key={k} style={{ borderRight: `1px solid ${C.ink}`, padding: '6px 16px', textAlign: 'center', minWidth: 110 }}>
              <div style={{ fontSize: 11.5, color: C.muted }}>{k}</div>
              <div style={{ fontSize: 16, fontWeight: 800, direction: 'ltr' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* الطرف */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 14 }}>
        <span style={{ fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>
          {t.receipt ? 'استلمنا من السيد/السادة:' : 'صرفنا إلى السيد/السادة:'}
        </span>
        <span style={{ flex: 1, borderBottom: `1px dotted ${C.ink}`, fontSize: 16, fontWeight: 600, padding: '0 6px 2px' }}>
          {party || ' '}
        </span>
      </div>

      {/* المبلغ */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'stretch', marginBottom: 14,
        border: `1px solid ${C.ink}`, borderRadius: 8, overflow: 'hidden',
      }}>
        <div style={{ background: C.soft, padding: '10px 16px', borderLeft: `1px solid ${C.ink}`, textAlign: 'center', minWidth: 170 }}>
          <div style={{ fontSize: 11.5, color: C.muted }}>مبلغ وقدره</div>
          <div style={{ fontSize: 22, fontWeight: 800, direction: 'ltr' }}>{money(total)} <span style={{ fontSize: 13 }}>SAR</span></div>
        </div>
        <div style={{ flex: 1, padding: '10px 12px', fontSize: 14.5, fontWeight: 600, display: 'flex', alignItems: 'center', lineHeight: 1.7 }}>
          {tafqeet(total) || '—'}
        </div>
      </div>

      {/* التفاصيل */}
      <table style={{ marginBottom: 14 }}>
        <tbody>
          <tr>
            <td style={th}>طريقة الدفع</td>
            <td style={cell}>{paymentName(voucher.paymentType)}</td>
            <td style={th}>{Number(voucher.paymentType) === 2 ? 'البنك' : 'الصندوق'}</td>
            <td style={cell}>{voucher.bankName || voucher.safeName || '—'}</td>
          </tr>
          {(voucher.referenceNumber || voucher.referenceDate) && (
            <tr>
              <td style={th}>رقم المرجع</td>
              <td style={cell}>{voucher.referenceNumber || '—'}</td>
              <td style={th}>تاريخ المرجع</td>
              <td style={{ ...cell, direction: 'ltr', textAlign: 'right' }}>
                {voucher.referenceDate ? dayjs(voucher.referenceDate).format('DD/MM/YYYY') : '—'}
              </td>
            </tr>
          )}
          {hasTax && (
            <>
              <tr>
                <td style={th}>المبلغ قبل الضريبة</td>
                <td style={{ ...cell, direction: 'ltr', textAlign: 'right' }}>{money(net)}</td>
                <td style={th}>الضريبة {voucher.vatName ? `(${voucher.vatName})` : ''}</td>
                <td style={{ ...cell, direction: 'ltr', textAlign: 'right' }}>{money(tax)}</td>
              </tr>
              <tr>
                <td style={th}>الإجمالي</td>
                <td colSpan={3} style={{ ...cell, fontWeight: 800, direction: 'ltr', textAlign: 'right' }}>{money(total)} SAR</td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {/* البيان */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>وذلك عن:</div>
        <div style={{
          border: `1px solid ${C.line}`, borderRadius: 8, padding: '10px 12px', minHeight: 70,
          fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.8,
        }}>
          {voucher.description || voucher.notes || '—'}
        </div>
      </div>

      {/* التوقيعات */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginTop: 'auto', paddingTop: 28 }}>
        {signatures.map(s => (
          <div key={s} style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{s}</div>
            <div style={{ marginTop: 40, borderBottom: `1px solid ${C.ink}`, width: '80%', marginInline: 'auto' }} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>الاسم والتوقيع</div>
          </div>
        ))}
      </div>

      {/* التذييل */}
      <div style={{
        marginTop: 22, paddingTop: 6, borderTop: `1px solid ${C.line}`,
        display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted,
      }}>
        <span>طُبع من نظام أرباح | Profit ERP</span>
        <span style={{ direction: 'ltr' }}>{dayjs().format('DD/MM/YYYY HH:mm')}</span>
      </div>
    </div>
  );
});

VoucherPrint.displayName = 'VoucherPrint';
export default VoucherPrint;
